import { client } from '../client';
import { Cache, ResponseWithContentLength } from '../links/precacheLink';
import type {
  DefaultContext,
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { getPrecacheOperationKey } from './getPrecacheOperationKey';

export async function precacheOperation<
  TData,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  variables?: TVariables,
  options?: {
    refetch?: boolean;
    context?: DefaultContext;
  }
): Promise<Nullable<TData>> {
  const { data } = await client.query<TData, TVariables>({
    query,
    variables,
    fetchPolicy: 'no-cache',
    context: {
      ...(options?.context ?? {}),
      precachePolicy: options?.refetch === true ? 'cache-and-network' : 'cache-first',
      telemetry: { ...(options?.context?.telemetry ?? {}), fetchPolicy: 'no-cache' },
    },
  });
  return data;
}

export async function writePrecacheQuery<
  Q extends DocumentNode,
  V extends OperationVariables | undefined,
>({ query, variables, data }: { query: Q; variables: V; data: Q }): Promise<void> {
  const cache = await Cache.open();

  // We use the operation name and variables as the cache key
  const operationKey = getPrecacheOperationKey(query, variables);
  const operationUrl = new URL(operationKey, window.location.origin);

  const response = new ResponseWithContentLength(JSON.stringify(data));

  // Write the response to the cache
  await cache.put(operationUrl, response);
}

export async function deletePrecacheQuery<
  Q extends DocumentNode,
  V extends OperationVariables | undefined,
>({ query, variables }: { query: Q; variables?: V }): Promise<void> {
  const cache = await Cache.open();

  if (variables !== undefined) {
    const operationKey = getPrecacheOperationKey(query, variables);
    const operationUrl = new URL(operationKey, window.location.origin);
    await cache.delete(operationUrl);
  } else {
    const operationName = getMainDefinition(query).name?.value;
    if (!operationName) return;
    await cache.deleteByPrefix(operationName);
  }
}

/**
 * Update all precache entries for a given operation by applying a transform
 * to each cached response. Useful when a mutation changes a small part of a
 * large cached query and we want to preserve the cache for faster reloads
 * rather than evict it.
 *
 * The transform runs against the parsed JSON response. If it returns `null`,
 * the entry is left unchanged. Otherwise the transformed response is written
 * back to the same cache key.
 */
export async function updatePrecacheQuery<Q extends DocumentNode>({
  query,
  transform,
}: {
  query: Q;
  transform: (data: unknown) => unknown | null;
}): Promise<void> {
  const operationName = getMainDefinition(query).name?.value;
  if (!operationName) return;

  const cache = await Cache.open();
  const keys = await cache.keys();

  await Promise.all(
    keys
      .filter((key) => key.url.includes(operationName))
      .map(async (key) => {
        const cached = await cache.match(key);
        if (!cached) return;

        try {
          const data = await cached.json();
          const transformed = transform(data);
          if (transformed == null) return;
          const response = new ResponseWithContentLength(JSON.stringify(transformed));
          await cache.put(key, response);
        } catch {
          // If the response can't be parsed or updated, skip it rather than
          // corrupting the cache.
        }
      })
  );
}
